import { useEffect, useRef, useState } from 'react';

const SKILLS = [
  {
    category: 'Frontend',
    color: '#00f5ff',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    items: [
      { name: 'React / Next.js',          level: 90 },
      { name: 'HTML / CSS / Tailwind',    level: 95 },
      { name: 'TypeScript / JavaScript',  level: 88 },
    ],
  },
  {
    category: 'Backend',
    color: '#00ff88',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    items: [
      { name: 'Node.js / Express', level: 85 },
      { name: 'Spring Boot',       level: 75 },
      { name: 'FastAPI / Python',  level: 78 },
    ],
  },
  {
    category: 'Languages',
    color: '#ff00de',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
      </svg>
    ),
    items: [
      { name: 'C++ / C',  level: 85 },
      { name: 'Python',   level: 82 },
      { name: 'C# / PHP', level: 72 },
    ],
  },
  {
    category: 'DevOps & Tools',
    color: '#7c3aed',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
    items: [
      { name: 'Docker / CI-CD',       level: 80 },
      { name: 'MongoDB / PostgreSQL', level: 82 },
      { name: 'OpenGL / OpenCV',      level: 75 },
    ],
  },
];

const Skills = () => {
  const sectionRef = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" ref={sectionRef} className="py-20 px-2">
      {/* Heading */}
      <div className="reveal mb-14">
        <p className="text-[#00f5ff] text-xs font-mono tracking-[0.25em] uppercase mb-2">
          // what I work with
        </p>
        <h2
          className="section-title gradient-text text-4xl font-black mb-4"
        >
          Skills
        </h2>
        <div className="neon-line w-28" />
      </div>

      {/* Decorative background hex SVG */}
      <div className="absolute right-4 top-0 opacity-[0.04] pointer-events-none hidden xl:block select-none">
        <svg width="320" height="320" viewBox="0 0 320 320">
          {[0, 1, 2, 3].map((i) => (
            <polygon
              key={i}
              points="160,10 300,87 300,233 160,310 20,233 20,87"
              fill="none"
              stroke="#00f5ff"
              strokeWidth="1"
              transform={`scale(${1 - i * 0.2}) translate(${i * 32}, ${i * 32})`}
            />
          ))}
        </svg>
      </div>

      {/* Skill cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        {SKILLS.map((cat, ci) => (
          <div
            key={ci}
            className="neon-card p-6 reveal"
            style={{ transitionDelay: `${ci * 0.12}s` }}
          >
            {/* Category header */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="p-2 rounded-lg"
                style={{
                  color: cat.color,
                  background: `${cat.color}18`,
                  border: `1px solid ${cat.color}40`,
                }}
              >
                {cat.icon}
              </div>
              <h3
                className="text-sm font-bold uppercase tracking-[0.2em]"
                style={{ color: cat.color, fontFamily: "'Orbitron', monospace" }}
              >
                {cat.category}
              </h3>
            </div>

            {/* Skill rows */}
            <div className="space-y-5">
              {cat.items.map((skill, si) => (
                <div key={si}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-300 font-medium">{skill.name}</span>
                    <span
                      className="text-xs font-mono font-bold"
                      style={{ color: cat.color }}
                    >
                      {skill.level}%
                    </span>
                  </div>
                  <div className="skill-bar-bg">
                    <div
                      className="skill-bar-fill"
                      style={{
                        width: animated ? `${skill.level}%` : '0%',
                        background: `linear-gradient(90deg, ${cat.color}55, ${cat.color})`,
                        boxShadow: `0 0 10px ${cat.color}66`,
                        transitionDelay: `${si * 0.15 + ci * 0.08}s`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Skills;
